import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  runInInjectionContext,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import {
  MatChipSelectionChange,
  MatChipsModule,
} from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import {
  MatSelect,
  MatSelectChange,
  MatSelectModule,
} from '@angular/material/select';
import { PuebloBase } from '@components/pueblo-base.component';
import { VIfDirective } from '@utils/v-if.directive';
import { Pueblo } from 'pueblo-types';
import { combineLatest, map, of, startWith, switchMap } from 'rxjs';
import {
  assertIsPuebloSelectForm,
  IPuebloSelectForm,
  NotifyPuebloOption,
  PuebloOption,
} from '../form-types';

@Component({
  selector: 'pueblo-select',
  templateUrl: 'pueblo-select.component.html',
  styleUrl: 'pueblo-select.component.scss',
  imports: [
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    VIfDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PuebloSelect extends PuebloBase {
  private readonly rootForm = inject(FormGroupDirective);

  protected readonly NotifyPuebloOption = NotifyPuebloOption;

  private readonly $notifyPuebloSelect =
    viewChildren<MatSelect>('notifyPuebloSelect');

  readonly $notifyInterestDisabled = input(true, {
    alias: 'notifyInterestDisabled',
  });

  readonly puebloSelected = output<PuebloOption>();

  readonly $formPath = input.required<string[]>({ alias: 'formPath' });

  protected form!: FormGroup<IPuebloSelectForm>;

  protected get selectedPueblosControl(): FormControl<
    ReadonlyArray<PuebloOption>
  > {
    return this.form.controls.selectedPueblos;
  }

  protected readonly $puebloOptions = signal<PuebloOption[]>([]);

  protected readonly $disabled = signal(false);

  ngOnInit(): void {
    const form = this.rootForm.control.get(this.$formPath());
    assertIsPuebloSelectForm(form);
    this.form = form;

    runInInjectionContext(this.injector, () => {
      combineLatest([
        this.httpStore.appUserJoinedPueblos$,
        this.form.controls.selectedPueblos.valueChanges.pipe(
          startWith(this.form.controls.selectedPueblos.value),
        ),
      ])
        .pipe(
          map(([joinedPueblos, selectedPueblos]) =>
            joinedPueblos
              .filter(
                (pueblo) =>
                  pueblo.config.postingPermissions ===
                    Pueblo.Config.PostingPermissions.ALL_MEMBERS ||
                  (pueblo.config.postingPermissions ===
                    Pueblo.Config.PostingPermissions.OWNERS &&
                    pueblo.owners.includes(this.$appUser().resourceName)),
              )
              .map((pueblo) => ({
                pueblo,
                selected: selectedPueblos.some(
                  (option) =>
                    option.pueblo.resourceName === pueblo.resourceName,
                ),
                notify: NotifyPuebloOption.EVERYONE,
              }))
              .sort((a, b) => {
                if (a.pueblo.resourceName === this.$appUser().mainPueblo)
                  return -1;
                if (
                  this.$appUser().ownedPueblos.includes(a.pueblo.resourceName)
                )
                  return -1;
                return b.pueblo.members.length - a.pueblo.members.length;
              }),
          ),
          switchMap((options) => {
            return combineLatest(
              options.map((option) => {
                if (
                  option.pueblo.imageUrl ||
                  option.pueblo.config.postingPermissions !==
                    Pueblo.Config.PostingPermissions.OWNERS
                )
                  return of(option);
                return combineLatest([
                  of(option),
                  this.httpStore.user$(option.pueblo.owners[0]),
                ]).pipe(
                  map(([option, owner]) => ({
                    ...option,
                    pueblo: {
                      ...option.pueblo,
                      imageUrl: owner?.profilePictureUrl ?? '',
                    },
                  })),
                );
              }),
            );
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(this.$puebloOptions.set);
    });
  }

  constructor() {
    super();

    effect(() => {
      if (this.$notifyInterestDisabled()) {
        this.$notifyPuebloSelect().forEach((select) => {
          select.value = NotifyPuebloOption.EVERYONE;
        });
      }
    });
  }
  protected handlePuebloSelectionChange(
    change: MatChipSelectionChange,
    option: PuebloOption,
  ): void {
    if (!change.isUserInput) return;
    if (change.selected) {
      this.selectPuebloOption(option);
    } else {
      this.deselectPuebloOption(option);
    }
  }

  protected handlePuebloNotificationChange(
    change: MatSelectChange,
    option: PuebloOption,
  ): void {
    this.selectPuebloOption({
      ...option,
      notify: change.value as NotifyPuebloOption,
    });
  }

  protected selectPuebloOption(option: PuebloOption): void {
    this.selectedPueblosControl.setValue([
      option,
      ...this.selectedPueblosControl.value.filter(
        (selectedOption) =>
          selectedOption.pueblo.resourceName !== option.pueblo.resourceName,
      ),
    ]);
    this.puebloSelected.emit(option);
  }

  protected deselectPuebloOption(option: PuebloOption): void {
    this.selectedPueblosControl.setValue(
      this.selectedPueblosControl.value.filter(
        (selectedOption) =>
          selectedOption.pueblo.resourceName !== option.pueblo.resourceName,
      ),
    );
  }
}
